/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineOppositeContent';

const TimelineOppositeContentManager: ComponentManagerFn<
  'TimelineOppositeContent'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'TimelineOppositeContent'>(
      platform,
      'TimelineOppositeContent',
    ),
    type: 'TimelineOppositeContent',
    name: 'Timeline Opposite Content',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnClick',
          'BoxContainer',
          'Box',
          'Typography',
          'H1',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {},
    getDisplayText: (item) => 'Opposite content',
    category: 'Forms',
    icon: { iconName: 'text' },
    description: `**Overview**<br/>
The Timeline Opposite Content component is a layout container used specifically within **Timeline** visualizations. It is positioned on the **opposite** side of the central axis (the connector line and dot) relative to the main **Timeline Content**.
Use this component to display secondary metadata—most commonly dates or timestamps—so that it sits distinct from the main event description. For example, if the event description ("Order Shipped") is on the right, the timestamp ("10:00 AM") can be placed on the left using this component.

**1. Layout & Positioning**<br/>
This component works in tandem with **TimelineContent** to create a balanced, symmetrical look.
* **Positioning:** If your timeline is aligned to the center, this content appears on the left (or right) side of the dot, mirroring the main content.
* **Flexibility:** It functions as a generic container, meaning you can place text, headers, or boxes inside it to match your design requirements.

**2. Supported Children**<br/>
Since it has no specific visual properties of its own, its appearance is defined entirely by what you nest inside it.
* **Typography / H1:** Use these to render the actual text (e.g., "Jan 12, 2024").
* **Box / BoxContainer:** Use these to create more complex layouts or add specific padding/margins.
* **TriggerOnClick:** Allows the opposite content area to be clickable for navigation or to reveal more context.

**3. Best Use Cases**<br/>
* **Timestamps:** Displaying the exact time ("09:42 AM") opposite the event description for chronological precision.
* **Dates:** Grouping events by day ("Mon, Oct 5") on the left axis while the right axis shows the activities.
* **User Avatars:** Showing the profile picture of the user who performed the action opposite the action details.
* **Duration:** Indicating how long a step took ("2 hrs") opposite the step name in a process flow.`,
  };
};

export default TimelineOppositeContentManager;
